import { type CraftSchema } from '@/schemas/craft/create';
import { api } from './api';
import axios from 'axios';


// POPULATE FORM
export interface PopulateFormResponse {
  success: boolean;
  country: string;
  country_code: string;
  flow_steps: FlowSteps;
}
interface FlowSteps {
  step_1_actions: Step1Action[];
  step_2_formats: Step2Format[];
}
interface Step2Format {
  format_type: string;
  name: string;
  description: string;
  resolution: string;
  icon: string;
  available: boolean;
}
interface Step1Action {
  id: string;
  name: string;
  description: string;
  icon: string;
  available: boolean;
}

// GET TAXONOMIES
export interface TaxonomyResponse {
  success: boolean;
  format_type: string;
  format_display: string;
  country: string;
  taxonomies: Taxonomy[];
  total_taxonomies: number;
}
interface Taxonomy {
  id: number;
  name: string;
  display_name: string;
  format_type: string;
  resolution: string;
  taxonomy: Record<string, H1 | null>;
  visual_preview: VisualPreview;
  specifications: Record<string, Specification | null>;
}
interface Specification {
  text: string;
  color: string | null;
}
interface VisualPreview {
  description: string[];
  elements_layout: Record<string, H12 | null>;
  preview_image_url: string | null;
  dynamic_preview_url: string;
  has_stored_preview: boolean;
}
interface Coordinates {
  x: string;
  y: string;
}
interface H1 {
  position: string;
  size: number;
}
interface H12 extends H1 {
  coordinates: Coordinates;
}

// SAVE CRAFT
interface SaveCraftResponse {
  success: boolean;
  process_image_id: number;
  results: ResultSaveCraft[];
  job_id: string;
  status: string;
  total_images: number;
  message: string;
  celery_task_id: string;
}

interface ResultSaveCraft {
  index: number;
  is_combo: boolean;
  success: boolean;
  errors?: string[];
}


export const craftService = {
  getTaxonomies: async (formatType: string): Promise<TaxonomyResponse> => {
    try {
      const response = await api.get(`/image-creation/taxonomies?format_type=${formatType}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener lista de taxonomias:', error);
      throw error;
    }
  },
  populateForm: async (): Promise<PopulateFormResponse> => {
    try {
      const response = await api.get('/image-creation/creation-flow');
      return response.data;
    } catch (error) {
      console.error('Error al obtener lista de manualidades:', error);
      throw error;
    }
  },
  saveForm: async (craft: CraftSchema): Promise<{ success: boolean, message: string, id?: number, job_id?:string, celery_task_id?: string, isCombo?: boolean }> => {
    const formData = new FormData();

    for (const key in craft) {
      const value = craft[key as keyof CraftSchema];
      if (!value) continue;

      switch (key) {
        case 'formatType':
          formData.append('format_type', value);
          break;
        case 'image_rule_id':
          formData.append('rule_id', value);
          break;
        case 'taxonomy':
          formData.append(key, value);
          break;
        case 'elementType':
          break;
        default:
          formData.append(key, value);
          break;
      }
    }

    try {
      const response = await api.post<SaveCraftResponse>('/images/bulk-generate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return {
        success: true,
        message: 'Manualidad guardada correctamente',
        id: response.data.process_image_id,
        isCombo: response.data.results?.[0]?.is_combo || false,
        job_id: response.data.job_id,
        celery_task_id: response.data.celery_task_id
      }
    } catch (error) {
      console.error('Error al guardar manualidad:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: (typeof error.response?.data?.error === 'string'
            ? error.response?.data?.error
            : Object.values(error.response?.data?.errors || {}).join(', '))
            || 'Error al comunicarse con el servidor'
        };
      }
      return {
        success: false,
        message: 'Error inesperado al guardar la manualidad'
      };
    }
  },
  getStatusBulkGenerate: async (job_id:string): Promise<{ status: 'success' | 'processing' | 'started' |'error', process_image_id: number }> => {
    try {
      const response = await api.get(`/images/bulk-jobs/${job_id}`);

      return {
        status: response.data.status,
        process_image_id: response.data.process_image_id
      };
    } catch (error) {
      console.error('Error al descargar combos:', error);
      return {
        status: 'error',
        process_image_id: 0
      };
    }
  }
};
